import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

/**
 * VoiceDictation component - Adds speech-to-text functionality
 * Uses Web Speech API (browser native)
 */
const VoiceDictation = ({ 
  onTranscript, 
  className,
  disabled = false,
  placeholder = "Cliquez pour dicter..."
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Check for Web Speech API support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'fr-FR';

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptPart = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcriptPart + ' ';
          } else {
            interimTranscript += transcriptPart;
          }
        }

        const fullTranscript = finalTranscript || interimTranscript;
        setTranscript(fullTranscript);
        
        if (finalTranscript && onTranscript) {
          onTranscript(finalTranscript.trim());
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          toast.error('Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
        } else if (event.error === 'no-speech') {
          toast.info('Aucune voix détectée. Réessayez.');
        } else {
          toast.error('Erreur de reconnaissance vocale');
        }
      };

      recognitionRef.current.onend = () => {
        if (isListening) {
          // Restart if still supposed to be listening
          try {
            recognitionRef.current.start();
          } catch (e) {
            setIsListening(false);
          }
        }
      };
    } else {
      setIsSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onTranscript, isListening]);

  const toggleListening = () => {
    if (!isSupported) {
      toast.error('La dictée vocale n\'est pas supportée par votre navigateur');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      setTranscript('');
    } else {
      setTranscript('');
      try {
        recognitionRef.current.start();
        setIsListening(true);
        toast.info('Dictée activée - Parlez maintenant');
      } catch (error) {
        console.error('Error starting recognition:', error);
        toast.error('Impossible de démarrer la dictée');
      }
    }
  };

  if (!isSupported) {
    return null; // Don't render if not supported
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Button
        type="button"
        variant={isListening ? "default" : "outline"}
        size="icon"
        onClick={toggleListening}
        disabled={disabled}
        className={cn(
          "relative transition-all",
          isListening && "bg-red-500 hover:bg-red-600 animate-pulse"
        )}
        title={isListening ? "Arrêter la dictée" : "Démarrer la dictée vocale"}
      >
        {isListening ? (
          <MicOff className="w-4 h-4" />
        ) : (
          <Mic className="w-4 h-4" />
        )}
        {isListening && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full animate-ping" />
        )}
      </Button>
      
      {isListening && transcript && (
        <span className="text-sm text-slate-500 italic truncate max-w-[200px]">
          {transcript}...
        </span>
      )}
    </div>
  );
};

export default VoiceDictation;
